/**
 * SignaturePad - Área para desenhar a assinatura na tela (mouse, dedo ou caneta)
 *
 * Reutilizável por qualquer consumidor do Core de Aceites. Gera PNG com fundo branco.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial (Aceites Fase 3).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface SignaturePadHandle {
  /** PNG em data URL, ou null quando vazio */
  toDataUrl: () => string | null;
  clear: () => void;
}

interface Props {
  onChange?: (hasDrawing: boolean) => void;
  disabled?: boolean;
}

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(({ onChange, disabled }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const activePointer = useRef<number | null>(null);
  const moved = useRef(false);
  const strokes = useRef(0);
  const [empty, setEmpty] = useState(true);

  // Ajusta a resolução ao tamanho real (nitidez em telas de alta densidade)
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const r = c.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, r.width, r.height);
    strokes.current = 0;
    drawing.current = false;
    activePointer.current = null;
    setEmpty(true);
    onChange?.(false);
  };

  /** Encerra o traço mesmo em cancelamento/perda de captura, sem deixar desenho preso. */
  const finishStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || activePointer.current !== e.pointerId) return;
    drawing.current = false;
    activePointer.current = null;
    if (moved.current) {
      strokes.current += 1;
      setEmpty(false);
      onChange?.(true);
    }
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  useImperativeHandle(ref, () => ({
    toDataUrl: () => (strokes.current < 1 ? null : canvasRef.current?.toDataURL('image/png') ?? null),
    clear,
  }));

  return (
    <div className="space-y-1.5">
      {/* Impede que os eventos usados para desenhar ativem gestos dos ancestrais. */}
      <div className="relative touch-none rounded-md border border-dashed bg-background"
        onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()} onTouchCancel={(e) => e.stopPropagation()}>
        <canvas
          ref={canvasRef}
          className="block h-40 w-full touch-none rounded-md cursor-crosshair"
          aria-label="Área de assinatura"
          onPointerDown={(e) => {
            if (disabled || drawing.current || !e.isPrimary) return;
            const ctx = e.currentTarget.getContext('2d');
            if (!ctx) return;
            e.preventDefault();
            e.stopPropagation();
            drawing.current = true;
            moved.current = false;
            activePointer.current = e.pointerId;
            e.currentTarget.setPointerCapture(e.pointerId);
            const r = e.currentTarget.getBoundingClientRect();
            ctx.beginPath();
            ctx.moveTo(e.clientX - r.left, e.clientY - r.top);
          }}
          onPointerMove={(e) => {
            if (disabled || !drawing.current || activePointer.current !== e.pointerId) return;
            const ctx = e.currentTarget.getContext('2d');
            if (!ctx) return;
            e.preventDefault();
            e.stopPropagation();
            const r = e.currentTarget.getBoundingClientRect();
            ctx.lineTo(e.clientX - r.left, e.clientY - r.top);
            ctx.stroke();
            moved.current = true;
          }}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
          onLostPointerCapture={finishStroke}
        />
        {empty && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
            Desenhe sua assinatura aqui
          </span>
        )}
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={clear} disabled={disabled || empty}>
          <Eraser className="mr-1 h-3.5 w-3.5" />Limpar
        </Button>
      </div>
    </div>
  );
});
SignaturePad.displayName = 'SignaturePad';
